import { ArrowLeft, RefreshCw } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
import TVChannelsLayout from '@/pages/content/TVChannelsLayout';
import Button from '@/components/ui/Button';
import DataTable from '@/components/ui/DataTable';
import { TableColumn } from '@/types';
import {
  FlussonicServerDetails,
  FlussonicStreamDetails,
} from '@/services/flussonicServerService';
import { useFlussonicServerDetails } from '@/hooks/useFlussonicServers';

const formatNumber = (value: number | null | undefined) =>
  value === null || value === undefined ? 'N/A' : new Intl.NumberFormat('es-DO').format(value);

const formatBytes = (value: number | null | undefined) => {
  if (value === null || value === undefined) {
    return 'N/A';
  }

  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let current = value;
  let unitIndex = 0;

  while (current >= 1024 && unitIndex < units.length - 1) {
    current /= 1024;
    unitIndex += 1;
  }

  return `${current.toFixed(unitIndex === 0 ? 0 : 2)} ${units[unitIndex]}`;
};

const formatBandwidth = (value: number | null | undefined) => {
  if (value === null || value === undefined) {
    return 'N/A';
  }

  return `${formatBytes(value)}/s`;
};

const formatUnixSeconds = (value: number | null | undefined) => {
  if (value === null || value === undefined) {
    return 'N/A';
  }

  const date = new Date(value * 1000);
  if (Number.isNaN(date.getTime())) {
    return 'N/A';
  }

  return date.toLocaleString('es-DO');
};

const StatCard = ({ label, value }: { label: string; value: string | number }) => (
  <div className="border border-gray-200 bg-white p-4 shadow-sm">
    <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</div>
    <div className="mt-2 text-2xl font-semibold text-gray-900">{value}</div>
  </div>
);

const renderStatusBadge = (enabled: boolean | null, activeText: string, inactiveText: string) => (
  <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${
    enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-700'
  }`}>
    {enabled ? activeText : inactiveText}
  </span>
);

const buildColumns = (): TableColumn<FlussonicStreamDetails>[] => [
  {
    key: 'name',
    header: 'Stream',
    accessor: (stream) => (
      <div>
        <div className="font-medium text-gray-900">{stream.title || stream.name || 'Sin nombre'}</div>
        {stream.name && stream.title && (
          <div className="text-xs text-gray-500">{stream.name}</div>
        )}
      </div>
    ),
  },
  {
    key: 'alive',
    header: 'Activo',
    accessor: (stream) => renderStatusBadge(stream.alive === true, 'Sí', 'No'),
  },
  {
    key: 'running',
    header: 'Corriendo',
    accessor: (stream) => renderStatusBadge(stream.running === true, 'Sí', 'No'),
  },
  {
    key: 'clientCount',
    header: 'Clientes',
    accessor: (stream) => <span className="text-sm text-gray-900">{formatNumber(stream.clientCount)}</span>,
  },
  {
    key: 'outBandwidth',
    header: 'Salida',
    accessor: (stream) => <span className="text-sm text-gray-900">{formatBandwidth(stream.outBandwidth)}</span>,
  },
  {
    key: 'retryCount',
    header: 'Reintentos',
    accessor: (stream) => <span className="text-sm text-gray-900">{formatNumber(stream.retryCount)}</span>,
  },
  {
    key: 'lastAccessAt',
    header: 'Último acceso',
    accessor: (stream) => <span className="text-sm text-gray-900">{formatUnixSeconds(stream.lastAccessAt)}</span>,
  },
];

const SummaryCards = ({ details }: { details: FlussonicServerDetails }) => (
  <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
    <StatCard label="Streams" value={formatNumber(details.streamCount)} />
    <StatCard label="Streams activos" value={formatNumber(details.activeStreamCount)} />
    <StatCard label="Streams corriendo" value={formatNumber(details.runningStreamCount)} />
    <StatCard label="Clientes activos" value={formatNumber(details.totalClients)} />
    <StatCard label="Ancho de banda salida" value={formatBandwidth(details.totalOutBandwidth)} />
    <StatCard label="Bytes entrada" value={formatBytes(details.totalBytesIn)} />
    <StatCard label="Bytes salida" value={formatBytes(details.totalBytesOut)} />
    <StatCard label="Estado configurado" value={details.enabled ? 'Activo' : 'Inactivo'} />
  </div>
);

const TVChannelsFlussonicServerDetails = () => {
  const { serverId } = useParams();
  const { data: details, isLoading, isError, error, refetch, isFetching } = useFlussonicServerDetails(serverId);
  const columns = buildColumns();

  return (
    <TVChannelsLayout>
      <div className="space-y-4">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <Link to="/content/tv-channels/flussonic-servers" className="mb-2 inline-flex items-center text-sm font-medium text-indigo-600 hover:text-indigo-700">
              <ArrowLeft className="mr-1 h-4 w-4" />
              Volver a servidores
            </Link>
            <h1 className="text-2xl font-semibold text-gray-900">
              {details?.name ?? 'Servidor Flussonic'}
            </h1>
            {details?.baseUrl && (
              <p className="mt-1 text-sm text-gray-500">{details.baseUrl}</p>
            )}
          </div>
          <Button size="sm" variant="outline" onClick={() => refetch()} disabled={isFetching || isLoading}>
            <RefreshCw className={`mr-1 h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
            Refrescar
          </Button>
        </div>

        {isError && (
          <div className="rounded-md bg-red-50 p-4 text-sm text-red-700">
            {error instanceof Error ? error.message : 'Error al cargar los detalles del servidor'}
          </div>
        )}

        {isLoading && (
          <div className="border border-gray-200 bg-white px-4 py-8 text-center text-sm text-gray-500 shadow">
            Cargando detalles del servidor...
          </div>
        )}

        {details && (
          <>
            <SummaryCards details={details} />
            <DataTable
              data={details.streams}
              columns={columns}
              paginate
              itemsPerPage={25}
              tableStyle="compact"
              searchPlaceholder="Buscar stream..."
              title="Streams"
              className="shadow"
            />
          </>
        )}
      </div>
    </TVChannelsLayout>
  );
};

export default TVChannelsFlussonicServerDetails;
